import { allowTracking } from '@/helpers';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '.';
import { useConfidenceClient } from './useConfidenceExperiment';

/**
 * Custom hook to get Confidence flags with SSR support
 * @param flagKey - The feature flag key to resolve
 * @param defaultValue - Default value to use while loading or if flag fails
 * @param shouldFetch - Whether to fetch the flag from Confidence client
 * @returns An object with flagValue and isReady (indicating if the flag is ready to be used)
 */
export function useConfidenceFlag<T = any>(
  flagKey: string,
  defaultValue: T,
  shouldFetch: boolean = true,
): { flagValue: T; isReady: boolean } {
  const confidence = useConfidenceClient();

  // To know if user have accepted tracking coookies before mounting to avoid flickering
  const initialAllowTracking = useMemo(() => allowTracking(), []);

  // Get SSR-preloaded flags from Redux store
  const ssrFlags = useAppSelector((state) => state.flags);
  const ssrFlagValue = ssrFlags?.[flagKey]?.payload;
  const hasFetchedUser = useAppSelector((state) => state.users.hasFetched);

  const hasSSRValue = ssrFlagValue !== undefined;

  // Initialize with SSR value if available, otherwise use default
  const initialValue = hasSSRValue ? ssrFlagValue : defaultValue;
  const [flagValue, setFlagValue] = useState<T>(initialValue as T);
  // Ready to render if we have SSR value or tracking is disabled (no need to fetch)
  const [isReady, setIsReady] = useState<boolean>(hasSSRValue || !initialAllowTracking);

  useEffect(() => {
    if (!shouldFetch) {
      return;
    }

    if (!confidence?.isReady || !hasFetchedUser || !initialAllowTracking) {
      return;
    }

    const fetchFlag = async () => {
      try {
        const value = await confidence.client.getFlag(flagKey, defaultValue as any);
        setFlagValue(value as T);
        if (!isReady) {
          setIsReady(true);
        }
      } catch (error) {
        console.warn(`Failed to fetch Confidence flag: ${flagKey}`, error);
      }
    };

    fetchFlag();
  }, [shouldFetch, confidence?.isReady, hasFetchedUser]);

  return { flagValue, isReady };
}

interface ConfidenceFlagContextValue<T> {
  flagValue: T;
  isReady: boolean;
}

const ConfidenceFlagContext = createContext<ConfidenceFlagContextValue<any> | null>(null);

interface ConfidenceFlagProviderProps<T> {
  flagKey: string;
  defaultValue: T;
  shouldFetch?: boolean;
  children: ReactNode;
}

export function ConfidenceFlagProvider<T = any>({
  flagKey,
  defaultValue,
  shouldFetch = true,
  children,
}: ConfidenceFlagProviderProps<T>) {
  const { flagValue, isReady } = useConfidenceFlag<T>(flagKey, defaultValue, shouldFetch);

  const value: ConfidenceFlagContextValue<T> = {
    flagValue,
    isReady,
  };

  return <ConfidenceFlagContext.Provider value={value}>{children}</ConfidenceFlagContext.Provider>;
}

export function useConfidenceFlagContext<T = any>(): ConfidenceFlagContextValue<T> {
  const context = useContext(ConfidenceFlagContext);

  if (!context) {
    throw new Error('useConfidenceFlagContext must be used within a ConfidenceFlagProvider');
  }

  return context as ConfidenceFlagContextValue<T>;
}
